import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { CircularProgress, IconButton, Tab, Tabs, Tooltip } from "@mui/material";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import "./OssActionHistoryPanel.css";

const recordFields = [
  ["action_at", "조치 일시", "compact"],
  ["contact_target", "확인 대상", "compact"],
  ["contact_channel", "확인 경로", "compact"],
  ["action_reason", "확인 사유", "wide"],
  ["checked_content", "확인 내용"],
  ["action_plan", "조치 내용"],
  ["next_plan", "후속 계획"],
  ["memo", "비고 / 메모"],
];

const isSystemHistory = (entry) => entry.history_type === "STATUS" ||
  (!entry.history_type && ["ORDER", "SCHEDULE"].includes(entry.title));
const followUpDate = (entry) => (entry.action_status || entry.description) === "IN_PROGRESS" ? entry.due_at?.slice(0, 10) : null;

export const OssActionHistoryPanel = ({
  rows,
  loading,
  error,
  formatTitle,
  formatStatus,
  lines = [],
}) => {
  const id = useId();
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("ACTION");
  const listRef = useRef(null);
  const backRef = useRef(null);
  const entryRefs = useRef([]);
  const position = useRef({ top: 0, index: null });
  const backPending = useRef(false);
  // Keep the open record stable when a newly saved note shifts the list.
  const selectedEntries = useRef(new Map());
  const row = selected === null ? null : selectedEntries.current.get(selected) || rows[selected];
  const title = (entry) =>
    formatTitle(entry.action_title || entry.title || "이력");
  const note = (entry) => entry.result_note || entry.note || "";
  const actionCount = rows.filter((entry) => !isSystemHistory(entry)).length;
  const filteredRows = rows.map((entry, index) => ({ entry, index })).filter(({ entry }) =>
    filter === "ALL" || (filter === "STATUS" ? isSystemHistory(entry) : !isSystemHistory(entry)));
  const changeFilter = (_, value) => {
    position.current = { top: 0, index: null };
    if (listRef.current) listRef.current.scrollTop = 0;
    setFilter(value);
  };
  const line =
    row &&
    lines.find(
      (entry) => String(entry.order_line_id) === String(row.order_line_id)
    );

  useEffect(() => {
    const onPopState = (event) => {
      backPending.current = false;
      const view = event.state?.__ossActionHistory;
      setSelected(view?.owner === id ? view.index : null);
    };
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
      // Remove only this panel's same-URL entry when the modal closes.
      if (
        window.history.state?.__ossActionHistory?.owner === id &&
        !backPending.current
      ) {
        window.history.back();
      }
    };
  }, [id]);

  useLayoutEffect(() => {
    if (selected !== null) {
      backRef.current?.focus({ preventScroll: true });
    } else if (position.current.index !== null) {
      if (listRef.current) listRef.current.scrollTop = position.current.top;
      entryRefs.current[position.current.index]?.focus({ preventScroll: true });
    }
  }, [selected]);

  const selectEntry = (index) => {
    selectedEntries.current.set(index, rows[index]);
    position.current = { top: listRef.current?.scrollTop || 0, index };
    const current = window.history.state || {};
    window.history.pushState(
      {
        ...current,
        ...(typeof current.idx === "number" ? { idx: current.idx + 1 } : {}),
        __ossActionHistory: { owner: id, index },
      },
      "",
      window.location.href
    );
    setSelected(index);
  };

  const returnToList = () => {
    if (backPending.current) return;
    if (window.history.state?.__ossActionHistory?.owner === id) {
      backPending.current = true;
      window.history.back();
    } else {
      setSelected(null);
    }
  };

  return (
    <section
      className="oss-action-history oss-history-browser"
      aria-labelledby={`${id}-title`}
    >
      <div className="oss-action-section-heading oss-history-heading">
        {row ? (
          <Tooltip title="이력 목록으로 돌아가기">
            <IconButton
              ref={backRef}
              aria-label="조치 이력 목록으로 돌아가기"
              onClick={returnToList}
              size="small"
            >
              <ArrowBackRoundedIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <HistoryOutlinedIcon />
        )}
        <h3 id={`${id}-title`}>{row ? "조치 이력 상세" : "조치 이력"}</h3>
        <span>
          {row ? "상세" : `${filteredRows.length}건`}
        </span>
      </div>
      {!row && <Tabs className="oss-history-filters" value={filter} onChange={changeFilter} aria-label="이력 종류" variant="fullWidth">
        <Tab value="ACTION" label={`통화·조치 ${actionCount}`} />
        <Tab value="STATUS" label={`시스템 ${rows.length - actionCount}`} />
        <Tab value="ALL" label={`전체 ${rows.length}`} />
      </Tabs>}
      {row ? (
        <div
          className="oss-action-list oss-history-detail"
          key="detail"
          role="region"
          aria-label="선택한 조치 이력 상세"
          tabIndex={0}
        >
          <h4>{title(row)}</h4>
          <dl className="oss-history-facts">
            <div>
              <dt>{row.occurred_at ? "기록 일시" : "확인 예정일"}</dt>
              <dd>{row.occurred_at || row.due_at || "-"}</dd>
            </div>
            <div>
              <dt>
                {row.history_type === "STATUS" ? "상태 변경" : "처리 상태"}
              </dt>
              <dd>{formatStatus(row)}</dd>
            </div>
            {(line || row.history_type === "ACTION") && (
              <div>
                <dt>기록 대상</dt>
                <dd>{row.order_line_id == null ? "주문 공통" : line?.item_name_snapshot || "품목 기록"}</dd>
              </div>
            )}
            {followUpDate(row) && <div><dt>다음 확인일</dt><dd>{followUpDate(row)}</dd></div>}
          </dl>
          <div className="oss-history-full-note">
            <h5>{row.history_type === "STATUS" ? "변경 사유" : "조치 기록"}</h5>
            {row.action_at ? (
              <dl className="oss-history-record-fields">
                {recordFields.map(([key, label, size]) => (
                  <div
                    key={key}
                    className={`oss-history-record-field ${size || "content"}`}
                  >
                    <dt>{label}</dt>
                    <dd>{row[key] || "-"}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p>{note(row) || "추가로 남긴 내용이 없습니다."}</p>
            )}
          </div>
        </div>
      ) : (
        <div
          className="oss-action-list oss-history-entries"
          key="list"
          ref={listRef}
          tabIndex={0}
          aria-label="조치 이력 목록"
          aria-busy={loading}
        >
          {loading ? (
            <div className="oss-action-empty">
              <CircularProgress size={22} aria-label="이력 불러오는 중" />
            </div>
          ) : filteredRows.length ? (
            filteredRows.map(({ entry, index }) => (
              <article className="oss-action-history-entry" key={index}>
                <button
                  type="button"
                  className="oss-history-open"
                  ref={(node) => {
                    entryRefs.current[index] = node;
                  }}
                  aria-label={`${title(entry)} ${
                    entry.occurred_at || entry.due_at || ""
                  } 상세 보기`}
                  onClick={() => selectEntry(index)}
                >
                  <span className="oss-history-preview">
                    <span className="oss-action-history-meta">
                      <strong>{entry.action_reason || title(entry)}</strong>
                      <time>
                        {entry.action_at ||
                          entry.occurred_at ||
                          entry.due_at ||
                          ""}
                      </time>
                    </span>
                    {!isSystemHistory(entry) && <span className="oss-history-contact">
                      <span><span className="oss-history-label">확인 대상</span>{entry.contact_target || "미기록"}</span>
                      <span><span className="oss-history-label">경로</span>{entry.contact_channel || "미기록"}</span>
                    </span>}
                    <span className="oss-history-status">
                      {[
                        entry.history_type === "ACTION" && entry.order_line_id == null ? "주문 공통" : null,
                        entry.order_line_id != null ? lines.find((line) => String(line.order_line_id) === String(entry.order_line_id))?.item_name_snapshot : null,
                        formatStatus(entry),
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                    {(entry.checked_content || entry.action_plan || entry.memo || note(entry)) && (
                      <span className="oss-history-excerpt">
                        {entry.checked_content || entry.action_plan || entry.memo || note(entry)}
                      </span>
                    )}
                    {(followUpDate(entry) || entry.next_plan) && <span className="oss-history-followup">
                      {followUpDate(entry) && <span><span className="oss-history-label">다음 확인</span><time>{followUpDate(entry)}</time></span>}
                      {entry.next_plan && <span className="oss-history-next-plan"><span className="oss-history-label">후속 계획</span>{entry.next_plan}</span>}
                    </span>}
                  </span>
                  <ChevronRightRoundedIcon className="oss-history-chevron" />
                </button>
              </article>
            ))
          ) : (
            <p className="oss-action-empty">
              {error
                ? "이력을 불러오지 못했습니다."
                : filter === "STATUS" ? "등록된 시스템 이력이 없습니다." : "등록된 조치 이력이 없습니다."}
            </p>
          )}
        </div>
      )}
    </section>
  );
};
